export interface TemplateComponent {
  type?: string;
  format?: string;
  text?: string;
  buttons?: { type?: string; text?: string }[];
  variable_labels?: string[];
}

export interface ParsedTemplateContent {
  header?: TemplateComponent;
  body?: TemplateComponent;
  footer?: TemplateComponent;
  buttons: string[];
  headerVarCount: number;
  bodyVarCount: number;
  headerLabels: string[];
  bodyLabels: string[];
}

export interface LeadTemplateContext {
  leadName?: string | null;
  leadId?: string | null;
  leadPhone?: string | null;
}

const META_VAR_RE = /\{\{\s*(\d+)\s*\}\}/g;

function findComponent(components: unknown[], type: string): TemplateComponent | undefined {
  return (components || []).find(
    (c) => String((c as TemplateComponent)?.type || "").toUpperCase() === type,
  ) as TemplateComponent | undefined;
}

/** Quantidade de variáveis sequenciais {{1}}…{{n}} no texto Meta. */
export function countMetaVariables(text: string | undefined | null): number {
  if (!text) return 0;
  const matches = String(text).match(META_VAR_RE) || [];
  if (matches.length === 0) return 0;
  const nums = matches.map((m) => parseInt(m.replace(/\D/g, ""), 10)).filter((n) => !Number.isNaN(n));
  return Math.max(...nums, 0);
}

export function labelsForComponent(
  comp: TemplateComponent | undefined,
  varCount: number,
  fallbackPrefix: string,
): string[] {
  const stored = Array.isArray(comp?.variable_labels) ? comp.variable_labels : [];
  return Array.from({ length: varCount }, (_, i) => {
    const label = stored[i]?.trim();
    if (label) return label;
    return `${fallbackPrefix} ${i + 1}`;
  });
}

export function parseTemplateContent(components: unknown[]): ParsedTemplateContent {
  const header = findComponent(components, "HEADER");
  const body = findComponent(components, "BODY");
  const footer = findComponent(components, "FOOTER");
  const buttonsComp = findComponent(components, "BUTTONS");

  const headerVarCount = header?.format === "TEXT" ? countMetaVariables(header?.text) : 0;
  const bodyVarCount = countMetaVariables(body?.text);

  return {
    header,
    body,
    footer,
    buttons: (buttonsComp?.buttons || [])
      .map((b) => String(b?.text || "").trim())
      .filter(Boolean),
    headerVarCount,
    bodyVarCount,
    headerLabels: labelsForComponent(header, headerVarCount, "Cabeçalho"),
    bodyLabels: labelsForComponent(body, bodyVarCount, "Informação"),
  };
}

export function substituteMetaVariables(text: string, values: string[]): string {
  return text.replace(META_VAR_RE, (_, numStr) => {
    const index = parseInt(numStr, 10) - 1;
    const value = values[index]?.trim();
    return value || `{{${numStr}}}`;
  });
}

/** Sugere valor com base no rótulo do campo e nos dados do lead. */
export function inferVariableDefault(
  label: string,
  index: number,
  context: LeadTemplateContext,
): string {
  const normalized = label
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}/gu, "");

  if (/nome|cliente|contato|destinat|usuario|usuário/.test(normalized)) {
    return context.leadName?.trim() || "";
  }
  if (/telefone|fone|whatsapp|celular|phone|numero|número/.test(normalized)) {
    return context.leadPhone?.trim() || "";
  }
  if (/pedido|order|codigo|código|refer|protocolo|id/.test(normalized)) {
    return context.leadId?.trim() || "";
  }

  if (index === 0 && context.leadName?.trim()) {
    return context.leadName.trim();
  }
  if (index === 1 && context.leadPhone?.trim()) {
    return context.leadPhone.trim();
  }

  return "";
}

export function buildDefaultVariableValues(
  labels: string[],
  context: LeadTemplateContext,
): string[] {
  return labels.map((label, index) => inferVariableDefault(label, index, context));
}

export function stripFabComponentMetadata(
  components: Record<string, unknown>[],
): Record<string, unknown>[] {
  return components.map((comp) => {
    const { variable_labels: _labels, ...rest } = comp;
    return rest;
  });
}
